import dayjs from 'dayjs'
import { v4 } from 'uuid'

import {
  ComcastErrorType,
  getFormattedOrdinals,
  getTitleAndSubtitle,
  noCacheUrl,
  platformStoreGet,
  platformStoreSet,
  secureUrl,
  stripHttpProtocol,
  trackError,
  triggerSentryError,
} from '../../../shared/helpers.js'
import configuration from '../configuration.js'
import googleAnalytics4 from '../lib/googleAnalytics'
import {
  appendUserId,
  baseRequest,
  contentServiceDelete,
  contentServiceGet,
  contentServicePatch,
  contentServicePut,
} from '../lib/utils.js'
import {
  extractAndReturnFavoriteStation,
  extractAndReturnStation,
} from '../lib/utils/station/stationUtils'
import Platform from '../platforms'
import { LocalizeStationStep } from '../reducers/types/reducerTypes'
import { ERROR_STATUS } from '../screens/ErrorScreen/types'
import { store } from '../store.js'

const deviceKey = `deviceId-ga`
const rootPath = '/'

export function getStationsByAuto() {
  const url = configuration.apiUrls.stationsByAuto
  return getLocalization(url)
}

export function getLocalizedStations() {
  return getLocalization(configuration.apiUrls.localizeStations)
}

export function getStationsByIpDefault() {
  const url = configuration.apiUrls.stationsByIp.replace(
    '[IP]',
    configuration.stationByIpDefault
  )
  return getLocalization(url)
}

export function getStationsByZip(zip) {
  const url = configuration.apiUrls.stationsByZip.replace('[ZIP]', zip)
  return getLocalization(url)
}

export function getStationDetails(stationId) {
  const url = configuration.apiUrls.stationDetails
    .replace('[DEVICETYPE]', Platform.deviceType)
    .replace('[STATION_ID]', stationId)

  return contentServiceGet(url)
}

export async function getSchedulesByStation(
  stationId,
  date,
  allLivestreams,
  addSchedulesToHome = false
) {
  try {
    const url = configuration.contentUrls.getSchedules
      .replace('[DEVICETYPE]', Platform.deviceType)
      .replace('[STATION_ID]', stationId)
      .replace('[DATE]', date)
      .replace('[ALL_LIVESTREAMS]', allLivestreams)
    const response = await contentServiceGet(url)
    const channels = response.collections[0].content[0].channels
    const areChannelsAvailable = channels.length > 0
    store.dispatch({
      type: 'SET_LIVESTREAM_AVAILABILITY',
      payload: areChannelsAvailable,
    })
    if (areChannelsAvailable) {
      store.dispatch({
        type: 'SET_SCHEDULES',
        payload: {
          channels: channels,
          lastDayFetched: date,
          stationId,
        },
      })
      if (addSchedulesToHome) {
        store.dispatch({
          type: 'ADD_SCHEDULES_TO_HOME',
          payload: {
            channels: channels,
          },
        })
      }
    }
  } catch (error) {
    console.error(error)
  }
}

export async function getNextDaySchedulesByStation() {
  try {
    const {
      multilivestream: { stationId, lastDayFetched },
    } = store.getState()
    if (stationId === null) return
    const nextDay = dayjs(lastDayFetched).add(1, 'days').format('YYYY-MM-DD')
    const url = configuration.contentUrls.getSchedules
      .replace('[DEVICETYPE]', Platform.deviceType)
      .replace('[STATION_ID]', stationId)
      .replace('[DATE]', nextDay)
      .replace('[ALL_LIVESTREAMS]', true)
    const response = await contentServiceGet(url)
    const channels = response.collections[0].content[0].channels
    store.dispatch({
      type: 'ADD_SCHEDULES',
      payload: {
        channels: channels,
      },
    })
  } catch (error) {
    console.error(error)
  }
}

export function getFavoriteStation(uid) {
  const url = configuration.apiUrls.favoriteStations
    .replace('[UID]', uid)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServiceGet(url, { ignoreCache: true })
}

export async function getUserStation(uid) {
  try {
    const response = await getFavoriteStation(uid)
    const content =
      response.collections &&
      response.collections[0] &&
      response.collections[0].content &&
      response.collections[0].content[0]
        ? response.collections[0].content[0]
        : null

    if (content) {
      return extractAndReturnFavoriteStation(response)
    } else {
      try {
        const autoLocalizationResponse = await getLocalizedStations()

        const stations = autoLocalizationResponse?.stations
        if (!stations || stations.length === 0) {
          throw new Error('No stations available from localization service')
        }

        const stationId = stations[0].staton_id

        if (stationId) {
          await setStation(stationId)
          const response = await getStationDetails(stationId)
          store.dispatch({
            type: 'UPDATE_LOCALIZATION_STEP',
            payload: LocalizeStationStep.RELOCALIZING,
          })
          const station = extractAndReturnStation(response)

          googleAnalytics4.setUserProperty(
            'station_localization',
            station.call_sign
          )

          return station
        }
      } catch (error) {
        const errorPayload = {
          error,
          status: ERROR_STATUS.LOCALIZATION_ERROR,
          content: {
            errorPath: rootPath,
            errorMessage:
              error.response?.message || error.message || 'Unknown error',
          },
          codeBaseLocation: 'contentAction',
          errorType: ComcastErrorType.network,
        }

        store.dispatch({
          type: 'UPDATE_LOCALIZATION_STEP',
          payload: LocalizeStationStep.ERROR,
        })

        store.dispatch({
          type: 'TRIGGER_ERROR',
          payload: errorPayload,
        })

        trackError(error)
      }
    }
  } catch (error) {
    console.error(error)
    trackError(error, 'getFavoriteStation catch clause')
    return Promise.reject(error)
  }
}

export function getShowPoster() {
  return contentServiceGet(
    'https://content.services.pbs.org/v3/samsungtv/collections/show-poster-highlight-fvod/'
  ).then((response) => response?.collections[0]?.content[0]?.image)
}

export function checkDeviceId() {
  let payload = {}
  const deviceId = platformStoreGet(deviceKey)

  if (deviceId) {
    payload.deviceId = platformStoreGet(deviceKey)
  } else {
    let deviceId

    if (COMPILING_FOR_VIZIO) {
      deviceId = `vizio-ga-${v4()}`
    } else if (COMPILING_FOR_COMCASTX1) {
      deviceId = `comcast-ga-${v4()}`
    } else if (COMPILING_FOR_LGTV) {
      deviceId = `lgtv-ga-${v4()}`
    } else {
      deviceId = `samsung-ga-${v4()}`
    }

    platformStoreSet(deviceKey, deviceId)

    payload.deviceId = deviceId
  }

  store.dispatch({
    type: 'UPDATE_DEVICE',
    payload,
  })
}

export function setStation(guid) {
  const url = configuration.apiUrls.setStation
    .replace('[UID]', platformStoreGet('uID-ga'))
    .replace('[GUID]', guid)
    .replace('[DEVICETYPE]', Platform.deviceType)

  if (MULTILIVESTREAM_ENABLED) {
    getSchedulesByStation(guid, new Date().toISOString().split('T')[0], true)
  }

  return contentServicePut(
    url,
    {
      flagship_station_id: guid,
    },
    { ignoreCache: true }
  )
}

export function getStationShowMenu(stationId) {
  return contentServiceGet(
    configuration.apiUrls.stationShowMenu
      .replace('[STATION_ID]', stationId)
      .replace('[DEVICETYPE]', Platform.deviceType)
  )
}

export function acceptVppa(uid) {
  const url = configuration.apiUrls.getUserInfo
    .replace('[uid]', uid)
    .replace('[DEVICETYPE]', Platform.deviceType)
  return contentServicePatch(url, { vppa_accepted: true })
}

export function declineVppa(uid) {
  const url = configuration.apiUrls.getUserInfo
    .replace('[uid]', uid)
    .replace('[DEVICETYPE]', Platform.deviceType)
  return contentServicePatch(url, { vppa_accepted: false })
}

export function getLocalization(url) {
  return baseRequest(url, 'GET', null, null, true, { ignoreCache: true })
    .then((resp) => {
      return Promise.resolve(resp)
    })
    .catch((err) => {
      console.error('Error getting localization data', err)
      return Promise.reject(err)
    })
}

export function getLocalizationVerificationCode(stationId) {
  const { localizationServiceAuth } = configuration.apiAuth
  const { localizationCodeService: localizationEndpoint } =
    configuration.apiUrls

  const data = { station_id: stationId }

  return baseRequest(
    localizationEndpoint,
    'POST',
    localizationServiceAuth,
    data,
    true,
    { contentType: 'application/json', ignoreCache: true }
  )
}

export function pollLocalizationAuthStatus(code) {
  const { localizationServiceAuth } = configuration.apiAuth
  const { localizationCodeService: localizationEndpoint } =
    configuration.apiUrls

  const url = `${localizationEndpoint}${code}/`

  return baseRequest(url, 'GET', localizationServiceAuth, null, true, {
    ignoreCache: true,
  })
}

export async function getFavorites(uid) {
  try {
    const response = await contentServiceGet(
      configuration.apiUrls.getUserFavorites
        .replace('[uid]', uid)
        .replace('[DEVICETYPE]', Platform.deviceType)
    )
    store.dispatch({
      type: 'SET_FAVORITES',
      payload: {
        content: response.collections[0]?.content || [],
        url: response.collections[0]?.url,
        nextUrl: response.meta.links.next,
      },
    })
    return
  } catch (error) {
    store.dispatch({
      type: 'SET_FAVORITES',
      payload: {
        content: 'error',
      },
    })
  }
}

export async function getAllFavorites(uid) {
  try {
    let response = await contentServiceGet(
      configuration.apiUrls.getUserFavorites
        .replace('[uid]', uid)
        .replace('[DEVICETYPE]', Platform.deviceType)
    )
    let acc = response.collections[0].content
    while (response.meta.links.next) {
      response = await contentServiceGet(response.meta.links.next)
      acc = acc.concat(response.collections[0].content)
    }
    store.dispatch({
      type: 'SET_FAVORITES',
      payload: {
        content: acc,
        url: response.collections[0].url,
        nextUrl: response.meta.links.next,
      },
    })
    return acc
  } catch (error) {
    store.dispatch({
      type: 'SET_FAVORITES',
      payload: {
        content: 'error',
      },
    })
  }
}

export function addToFavorites(uid, pageDetails) {
  const changeFavorites = configuration.contentUrls.changeFavorites
    .replace('[uid]', uid)
    .replace('[cid]', pageDetails.cid)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServicePut(changeFavorites, {})
    .then(() => {
      store.dispatch({
        type: 'ADD_FAVORITE',
        payload: { favorite: pageDetails },
      })
      store.dispatch({
        type: 'ADD_HOME_FAVORITE',
        payload: { favorite: pageDetails },
      })
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

export function removeFromFavorites(uid, cid) {
  const changeFavorites = configuration.contentUrls.changeFavorites
    .replace('[uid]', uid)
    .replace('[cid]', cid)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServiceDelete(changeFavorites)
    .then(() => {
      store.dispatch({
        type: 'DELETE_FAVORITE',
        payload: { cid },
      })
      store.dispatch({
        type: 'DELETE_HOME_FAVORITE',
        payload: { cid },
      })
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

export async function getWatchlist(uid) {
  try {
    const response = await contentServiceGet(
      configuration.apiUrls.getUserWatchlist
        .replace('[uid]', uid)
        .replace('[DEVICETYPE]', Platform.deviceType)
    )
    store.dispatch({
      type: 'SET_WATCHLIST',
      payload: {
        content: response.collections[0]?.content || [],
        url: response.collections[0]?.url,
        nextUrl: response.meta.links.next,
      },
    })
    return
  } catch (error) {
    store.dispatch({
      type: 'SET_WATCHLIST',
      payload: {
        content: 'error',
      },
    })
  }
}
export async function getAllWatchlist(uid) {
  try {
    let response = await contentServiceGet(
      configuration.apiUrls.getUserWatchlist
        .replace('[uid]', uid)
        .replace('[DEVICETYPE]', Platform.deviceType)
    )
    let acc = response.collections[0].content
    while (response.meta.links.next) {
      response = await contentServiceGet(response.meta.links.next)
      acc = acc.concat(response.collections[0].content)
    }
    store.dispatch({
      type: 'SET_WATCHLIST',
      payload: {
        content: acc,
        url: response.collections[0].url,
        nextUrl: response.meta.links.next,
      },
    })
    return acc
  } catch (error) {
    store.dispatch({
      type: 'SET_WATCHLIST',
      payload: {
        content: 'error',
      },
    })
  }
}

export function addToWatchList(watchlistItem) {
  const changeWatchlist = configuration.contentUrls.changeWatchlist
    .replace('[uid]', store.getState().user.pid)
    .replace('[cid]', watchlistItem.cid)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServicePut(changeWatchlist, {})
    .then(() => {
      store.dispatch({
        type: 'ADD_WATCHLIST',
        payload: { watchlist: watchlistItem },
      })
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

export function removeFromWatchList(videoId) {
  const changeWatchlist = configuration.contentUrls.changeWatchlist
    .replace('[uid]', store.getState().user.pid)
    .replace('[cid]', videoId)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServiceDelete(changeWatchlist, {})
    .then(() => {
      store.dispatch({
        type: 'DELETE_WATCHLIST',
        payload: { cid: videoId },
      })
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

export async function getWatchHistory(uid) {
  try {
    const url = configuration.apiUrls.getUserHistory
      .replace('[uid]', uid)
      .replace('[DEVICETYPE]', Platform.deviceType)
    let response = await contentServiceGet(url)
    store.dispatch({
      type: 'SET_WATCH_HISTORY',
      payload: {
        content: response.collections[0]?.content || [],
        url: response.collections[0]?.url,
        nextUrl: response.meta.links.next,
      },
    })
    return
  } catch (error) {
    store.dispatch({
      type: 'SET_WATCH_HISTORY',
      payload: {
        content: 'error',
      },
    })
  }
}

export async function getAllWatchHistory(uid) {
  try {
    let response = await contentServiceGet(
      configuration.apiUrls.getUserHistory
        .replace('[uid]', uid)
        .replace('[DEVICETYPE]', Platform.deviceType)
    )
    let acc = response.collections[0].content
    while (response.meta.links.next) {
      response = await contentServiceGet(response.meta.links.next)
      acc = acc.concat(response.collections[0].content)
    }
    store.dispatch({
      type: 'SET_WATCH_HISTORY',
      payload: {
        content: acc,
        url: response.collections[0].url,
        nextUrl: response.meta.links.next,
      },
    })
    return acc
  } catch (error) {
    store.dispatch({
      type: 'SET_WATCH_HISTORY',
      payload: {
        content: 'error',
      },
    })
  }
}

export function updateWatchHistory(videoId, timecode) {
  const url = configuration.apiUrls.getWatchProgress
    .replace('[uid]', store.getState().user.pid)
    .replace('[videoId]', videoId)
    .replace('[DEVICETYPE]', Platform.deviceType)
  const data = { timecode }

  store.dispatch({
    type: 'SET_VIDEO_SECONDS_WATCHED',
    payload: timecode,
  })

  return contentServicePut(url, data)
}

export function getCollections(
  url,
  identifier,
  exclude = [], //TODO: remove. nothing in GA currently utilized this argument in a meaningful way
  bypass404 = false
) {
  let localUrl = url
  const pid = store.getState().user.pid
  if (-1 === localUrl.indexOf('station_id') && -1 === localUrl.indexOf(pid)) {
    localUrl = appendUserId(localUrl, pid)
  }

  return contentServiceGet(localUrl)
    .then((resp) => {
      let collections = []
      if (resp.resource) {
        exclude.push(resp.resource.cid)
      }
      resp.collections.forEach((collection) => {
        if (collection.content) {
          let filteredContent = collection.content.filter(function (e) {
            return this.indexOf(e.cid) < 0
          }, exclude)
          collection.content = filteredContent
          collections.push(collection)
        }
      })

      collections = resp.collections.filter((collection) => {
        return collection.content && 0 !== collection.content.length
      })

      let collectionsResponse = {
        meta: resp.meta,
        ...(resp.resource && { resource: resp.resource }),
        collections,
      }
      return Promise.resolve(collectionsResponse)
    })
    .catch((err) => {
      if (!bypass404) {
        triggerSentryError(
          '[index] Error getting collections',
          'contentActions.js',
          {
            localUrl,
            errorMessage: err.message ?? 'Undefined error message (default)',
            errorLocation: 'Content Action - getCollections',
            errorType: ComcastErrorType.network,
          }
        )
      }

      return Promise.reject(err)
    })
}

export function getUpNext(videoId) {
  const {
    apiUrls: { getUpNext },
  } = configuration
  const url = getUpNext
    .replace('[uid]', store.getState().user.pid)
    .replace('[videoId]', videoId)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return new Promise((resolve, reject) => {
    getCollections(url, 'upNext')
      .then((response) => {
        const upNextData = response.resource
        resolve({
          upNextShowTitle:
            upNextData?.parent?.season?.show?.title ||
            upNextData?.parent?.show?.title,
          upNextEpisodeTitle: upNextData.title || upNextData.parent?.title,
          upNextEpisodeNumber: upNextData.ordinal || upNextData.parent?.ordinal,
          upNextSeasonNumber:
            upNextData?.parent?.season?.ordinal || upNextData?.parent?.ordinal,
          upNextImage: upNextData.images['asset-mezzanine-16x9'],
          upNextVideoType: upNextData.video_type,
          upNextUrl: encodeURIComponent(upNextData.url),
          isMvod: upNextData.flags?.is_mvod,
          isPlayable: upNextData.flags?.is_playable,
        })
      })
      .catch((err) => {
        reject(err)
      })
  })
}

export async function getHomeData(stationId, userId) {
  try {
    const response = await contentServiceGet(
      configuration.contentUrls.getHomeData
        .replace('[STATION_ID]', stationId)
        .replace('[DEVICETYPE]', Platform.deviceType)
    )
    //filter out collections that do not have the content attribute but they aren't stub collections
    const collections = response.collections.filter((collection) => {
      return (
        (collection.content && 0 !== collection.content.length) ||
        collection['collection_type'] === 'stub'
      )
    })

    store.dispatch({
      type: 'SET_HOME_CONTENT',
      payload: {
        collections,
      },
    })

    if (MULTILIVESTREAM_ENABLED) {
      const date = new Date().toISOString().split('T')[0]
      getSchedulesByStation(stationId, date, true, true)
    }

    store.dispatch({
      type: 'UPDATE_SCREENSAVER_IMAGES',
      payload: {
        collections,
      },
    })

    response.collections
      .filter((c) => c['collection_type'] === 'stub')
      .forEach(async (collection) => {
        try {
          const response = await contentServiceGet(
            collection.url.replace('{user_id}', userId)
          )
          response.collections?.forEach((collection) => {
            store.dispatch({
              type: 'UPDATE_HOME_CONTENT_COLLECTION',
              payload: {
                collection,
                meta: response.meta,
              },
            })
          })
        } catch (error) {
          console.error(error)
        }
      })
  } catch (error) {
    store.dispatch({
      type: 'SET_HOME_CONTENT',
      payload: {
        collections: 'error',
      },
    })
  }
}

export function getVideoDetails(url) {
  const { pid, ignoreIsPlayable } = store.getState().user
  const now = new Date()

  let trimmedUrl = url.split('?')[0]
  let relatedUrl = `${trimmedUrl}related`
  trimmedUrl = `${trimmedUrl}?user_id=${pid}`
  relatedUrl = `${relatedUrl}/?user_id=${pid}`
  trimmedUrl = stripHttpProtocol(noCacheUrl(trimmedUrl))
  relatedUrl = stripHttpProtocol(relatedUrl)

  const identifier = `video-details-${noCacheUrl(trimmedUrl)}`
  const promises = []
  promises.push(getCollections(trimmedUrl, identifier))
  promises.push(
    getCollections(
      relatedUrl,
      `video-details-related-${trimmedUrl}`,
      [],
      true
    ).catch((err) => {
      return err
    })
  )

  return Promise.all(promises)
    .then(([collectionsResponse, relatedResponse]) => {
      const out = {}

      const videoResource = collectionsResponse.resource
      const { showTitle, episodeTitle } = getTitleAndSubtitle(videoResource)

      out.assetMezzanine =
        videoResource.images['asset-mezzanine-16x9'] ||
        videoResource.images['asset-kids-mezzanine1-16x9']

      let tracks = []
      videoResource.closed_captions.forEach((track) => {
        if ('WebVTT' === track.profile) {
          let trackInfo = {
            src: secureUrl(track.url),
            label: `English ${track.profile}`,
            kind: 'captions',
          }
          tracks.push(trackInfo)
        }
      })
      out.captionTracks = tracks

      out.collections = collectionsResponse.collections

      out.content_rating = videoResource.content_rating

      out.contentScope = videoResource.parent?.season?.show?.audience?.[0].scope

      //Three types of parent object structure.
      out.parentShowSlug =
        videoResource.parent?.season?.show?.slug ||
        videoResource.parent?.show?.slug ||
        videoResource.parent?.slug

      out.contentType = videoResource.video_type

      out.description_long = videoResource.description_long

      out.duration = videoResource.duration

      out.episodeTitle = episodeTitle

      if (videoResource.expire_date) {
        //Coerce
        out.expire_date = new Date(videoResource.expire_date)
      } else {
        const tomorrow = new Date()
        tomorrow.setDate(now.getDate() + 1)
        out.expire_date = tomorrow
      }
      out.expired = now.getTime() >= out.expire_date

      out.formattedOrdinals = getFormattedOrdinals(videoResource)

      out.funderMessage = videoResource.funder_message

      out.is_mvod = collectionsResponse.resource.flags?.is_mvod

      out.isPlayable = videoResource.flags?.is_playable || ignoreIsPlayable
      out.isPlayableExplanation = videoResource.flags?.is_playable_explanation

      out.legacyTpMediaId = videoResource.legacy_tp_media_id.toString()

      // TODO: only check preview? what about related_links.length || related_promos.length ?
      out.hasRelated = relatedResponse?.resource?.video_type === 'preview'
      out.relatedAssetUrl = relatedResponse?.resource?.url
      out.relatedAssetVideoType = relatedResponse?.resource?.video_type

      out.seconds_watched = videoResource.seconds_watched

      out.seriesTitle = videoResource.parent?.season?.show?.title
        ? videoResource.parent?.season?.show?.title
        : videoResource.parent?.title

      out.seriesTrackingEvent =
        videoResource.parent?.season?.show?.tracking_ga_event

      out.short_description = videoResource.short_description

      out.showTitle = showTitle

      out.url = secureUrl(
        videoResource.dash_drm_videos?.[0]?.url ||
          videoResource.hls_videos?.[0]?.url ||
          ''
      )
      out.drmWidevideLicenseUrl =
        videoResource.dash_drm_videos?.[0]?.widevine_license

      out.videoId = videoResource.cid

      out.slug = videoResource.slug

      out.contentUrl = videoResource.url
      out.parent = videoResource.parent
      out.trickPlayUrl = videoResource.trick_play_files?.find(
        (trickFile) => trickFile.filetype === 'vtt'
      )?.url

      return out
    })
    .catch((err) => {
      triggerSentryError('getVideoDetails', 'contentActions.js', {
        trimmedUrl,
        relatedUrl,
        errorMessage: err.message ?? 'Undefined error message (default)',
        errorLocation: 'Content Action - getVideoDetails',
        errorType: ComcastErrorType.network,
      })

      return Promise.reject(err)
    })
}

export function getPrivacyPolicy() {
  const url = configuration.apiUrls.privacyPolicyText.replace(
    '[DEVICETYPE]',
    Platform.deviceType
  )
  return contentServiceGet(url)
    .then((data) => Promise.resolve(data))
    .catch((error) => Promise.reject({ error, url }))
}

export function getClosedCaptions() {
  const url = configuration.apiUrls.closedCaptionText.replace(
    '[DEVICETYPE]',
    Platform.deviceType
  )

  return contentServiceGet(url)
    .then((data) => Promise.resolve(data))
    .catch((error) => Promise.reject({ error, url }))
}

export function getTermsOfUse() {
  const url = configuration.apiUrls.termsOfUseText.replace(
    '[DEVICETYPE]',
    Platform.deviceType
  )

  return contentServiceGet(url)
    .then((data) => Promise.resolve(data))
    .catch((error) => Promise.reject({ error, url }))
}

export function getSearch(stationId, query) {
  const url = configuration.apiUrls.search
    .replace('[QUERY]', query)
    .replace('[STATION_ID]', stationId)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServiceGet(url).catch((error) => {
    triggerSentryError(
      `Error ${
        error.status ? error.status : '(unknown)'
      } on Search screen requesting content from ${url}`,
      'contentActions.js',
      {
        errorMesssage: error.message ?? 'Undefined error message (default)',
        errorLocation: 'Content Action - getSearch',
        errorType: ComcastErrorType.network,
      }
    )
  })
}

export function getMoreExtras(slug, stationId) {
  const url = configuration.contentUrls.getMoreExtras
    .replace('[slug]', slug)
    .replace('[STATION_ID]', stationId)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServiceGet(url)
    .then((resp) => Promise.resolve({ resp, url }))
    .catch((error) => Promise.reject({ error, url }))
}

export function getAvailableSeasons(slug, stationId) {
  const url = configuration.contentUrls.getAvailableSeasons
    .replace('[slug]', slug)
    .replace('[STATION_ID]', stationId)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServiceGet(url)
}

export function getExtrasInSeasons(
  slug,
  seasonId,
  stationId,
  activeSeason,
  url = null
) {
  if (!url) {
    url = configuration.contentUrls.getExtrasInSeasons
      .replace('[slug]', slug)
      .replace('[STATION_ID]', stationId)
      .replace('[SEASON_ID]', seasonId)
      .replace('[DEVICETYPE]', Platform.deviceType)
  }

  return getCollections(url, `season-${url}-${activeSeason}`).catch((error) =>
    Promise.reject({ error, url })
  )
}

export async function getPaginatedEpisodes(
  slug,
  seasonId,
  stationId,
  activeSeason
) {
  const url = configuration.contentUrls.getPaginatedEpisodes
    .replace('[slug]', slug)
    .replace('[SEASON_ID]', seasonId)
    .replace('[STATION_ID]', stationId)
    .replace('[DEVICETYPE]', Platform.deviceType)

  let fetchMore = true
  let next = url
  const allEpisodes = []

  try {
    while (fetchMore) {
      const response = await getCollections(
        next,
        `season-${url}-${activeSeason}`
      )

      if (response.collections.length === 0) {
        fetchMore = false
      } else {
        next = response.meta.links.next
        allEpisodes.push(...response.collections[0].content)

        if (!next) {
          fetchMore = false
        }
      }
    }
  } catch (error) {
    return Promise.reject({ error, url })
  }

  return Promise.resolve(allEpisodes)
}

export function getSeasonDetails(slug, seasonId, stationId) {
  const url = configuration.contentUrls.getSeasonDetails
    .replace('[slug]', slug)
    .replace('[STATION_ID]', stationId)
    .replace('[SEASON_ID]', seasonId)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServiceGet(url).catch((error) => Promise.reject({ error, url }))
}

export function getShowInfo(slug, stationId) {
  const url = configuration.contentUrls.getShowInfo
    .replace('[slug]', slug)
    .replace('[STATION_ID]', stationId)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServiceGet(url).catch((error) => Promise.reject({ error, url }))
}

export function getShows(stationId) {
  const url = configuration.contentUrls.getShows
    .replace('[STATION_ID]', stationId)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServiceGet(url)
}

export function getShowInfoByUser(slug, userId) {
  const url = configuration.contentUrls.getShowInfoByUser
    .replace('[slug]', slug)
    .replace('[USER_ID]', userId)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServiceGet(url)
}

const CONTINUE_WATCHING_SLUG = 'show-continue-watching'
const SEASON_ITEM_TYPE = 'season'
const SKYLAB_COLLECTION_ITEM_TYPE = 'skylab-collection'
const SHOW_CAROUSEL_SLUG = 'show-carousel'
const ASSET_MEZZANINE = 'asset-mezzanine-16x9'
const BACKGROUND_IMAGE = 'background'
const SHOW_MEZZANINE = 'show-mezzanine16x9'
const SHOW_LOGO = 'white-logo-41'

function initializePageData() {
  return {
    collections: [],
    pageDetails: {},
    continueWatchingData: {
      contentDescription: '',
      contentHasSecondsWatched: false,
      contentSecondsWatched: null,
      contentDuration: null,
      contentSeason: null,
      contentEpisode: null,
      contentTitle: '',
      contentMezzanineImage: '',
      contentUrl: '',
      contentResourceType: '',
    },
  }
}

export async function getShowDetails(slug, userId) {
  const pageData = initializePageData()

  try {
    const response = await getShowInfoByUser(slug, userId)
    if (response.collections?.length) {
      response.collections.forEach((collectionElement) => {
        if (collectionElement.slug === CONTINUE_WATCHING_SLUG) {
          setContinueWatchingCollection(pageData, collectionElement)
        } else if (collectionElement.item_type === SEASON_ITEM_TYPE) {
          const filteredCollection = filterContentPerCondition(
            collectionElement,
            hasEpisodesOrAssets
          )

          concatCollection(pageData, filteredCollection)
        } else if (
          collectionElement.item_type === SKYLAB_COLLECTION_ITEM_TYPE
        ) {
          const filteredCollection = filterContentPerCondition(
            collectionElement,
            hasVideos
          )

          concatCollection(pageData, filteredCollection)
        } else if (collectionElement.slug !== SHOW_CAROUSEL_SLUG) {
          concatCollection(pageData, collectionElement)
        }
      })

      setPageDetails(pageData, response)

      store.dispatch({
        type: 'SET_SHOW_DETAILS_DATA',
        payload: pageData,
      })
    }
  } catch (error) {
    console.error('err', error)
  }
}

function hasEpisodesOrAssets(seasonCollection) {
  return (
    seasonCollection.flags.has_episodes || seasonCollection.flags.has_assets
  )
}

function hasVideos(skylabCollection) {
  return skylabCollection.flags.has_videos
}

function setContinueWatchingCollection(pageData, collection) {
  if (collection.content.length !== 0) {
    const collectionContent = collection.content[0]

    pageData.continueWatchingData = {
      contentDescription: collectionContent?.description_short,
      contentHasSecondsWatched: collectionContent?.seconds_watched > 0,
      contentSecondsWatched: collectionContent?.seconds_watched,
      contentDuration: collectionContent?.duration,
      contentSeason: collectionContent?.parent?.season?.ordinal,
      contentEpisode: collectionContent?.parent?.ordinal,
      contentTitle: collectionContent?.title,
      contentMezzanineImage: collectionContent.images[ASSET_MEZZANINE] ?? null,
      contentUrl: collectionContent?.url,
      contentResourceType: collectionContent?.parent?.resource_type,
    }
  }
}

function setPageDetails(pageData, resp) {
  const { continueWatchingData } = pageData
  const pageDetails = resp.resource

  pageData.pageDetails = {
    ...pageDetails,
    mezzanineImage:
      (continueWatchingData?.contentMezzanineImage ||
        pageDetails.images[BACKGROUND_IMAGE] ||
        pageDetails.images[SHOW_MEZZANINE]) ??
      null,
    logo: pageDetails.images[SHOW_LOGO] ?? null,
  }
}

function filterContentPerCondition(collection, condition) {
  const collectionAux = { ...collection }
  collectionAux.content = collection.content.filter(condition)

  return collectionAux
}

function concatCollection(pageData, collection) {
  if (collection.content.length !== 0) {
    pageData.collections = pageData.collections.concat(collection)
  }
}

export async function getMessage() {
  const url = configuration.apiUrls.getMessageInformation.replace(
    '[DEVICETYPE]',
    Platform.deviceType
  )

  return contentServiceGet(url)
}

export async function getEvergreenMessaging() {
  const url = configuration.apiUrls.evergreenMessaging.replace(
    '[DEVICETYPE]',
    Platform.deviceType
  )

  return contentServiceGet(url, { ignoreCache: true })
}
